import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { serverSideTranslation } from '../util/next';
// import { newsCenterData } from '../data/newsCenter';
import NewsBanner from '../modules/NewsBanner';
import NewsType from '../modules/NewsType';
import NewsItemCenter from '../components/NewsList/NewsItemCenter';
import { getNewsCategory, getNewsPage, clientGetNewsPage } from '../api/newsApi';
import type { NewsCenterItem } from '../api/newsApi';
import { getPageContent } from '../api/pagesApi';
import Page from '../components/Layout/Page';
import { genShareConfig } from '../components/Share/use-config-share';
import ScrollPosition, { IScrollTopContext } from '../components/ScrollPosition';
import { BreakPoint, useScreenSize } from '../components/ScreenSize';
import { useScrollRequest } from '../hooks/useScrollRequest';

interface DataType {
  [key: string]: string;
}
interface Props {
  newsTypeList: Array<{
    typeName: string;
    typeVal: number;
  }>;
  newsCenterlist: NewsCenterItem[];
  total: number;
  pageContentData: any;
}

const pageSize = 10;

const NewsCenter = (props: Props) => {
  const { newsTypeList, newsCenterlist, total = 0, pageContentData } = props;
  const { t } = useTranslation('common');
  const newsCenterImg = 'https://p0.meituan.net/smartvenus/e4e4ec00bee1b19e71b563466b3c5e83150069.png';
  const router = useRouter();
  const { screen } = useScreenSize();
  const { locale = 'zh-CN' } = router;
  const [pageNo, setPageNo] = useState(1);
  // const url = new URLSearchParams(window.location.search);
  // console.log(url);

  const [newLists, setNewLists] = useState(newsCenterlist);
  const [category, setCategory] = useState<string | null>(null);
  const [newsTotal, setNewsTotal] = useState(total);
  const [isStick, setIsStick] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [loading, setLoading] = useState(false);
  const requestStatus = useRef<boolean>(false);

  const newsTextData: DataType = t('newsTextData', { returnObjects: true });
  const { shareContent } = pageContentData;

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    setCategory(queryParams.get('category') || '');
  }, []);

  //  获取更多新闻
  const getMoreNews = () => {
    setPageNo((prev) => {
      return prev + 1;
    });
  };

  const handleNewsTypeChange = (typeId: number | null) => {
    setCategory(typeId ? String(typeId) : '');
    setPageNo(1);
    document.documentElement.scrollTop = 0;
  };

  const handleScroll = (position: IScrollTopContext) => {
    const { scrollTop: top } = position;
    setScrollTop(top);
    switch (screen) {
      case BreakPoint.lg:
      case BreakPoint.xl:
      case BreakPoint['2xl']:
        setIsStick(false);
        break;
      default:
        setIsStick(top >= 124);
    }
  };

  const params = useMemo(() => {
    if (category === null) return null;
    return {
      pageSize,
      pageNo,
      newsClassifyId: category,
      lanType: locale,
    };
  }, [pageNo, category, locale]);

  const currentTagName = useMemo(() => {
    if (!category) return '全部';
    return newsTypeList.find((item) => item.typeVal === +category)?.typeName;
  }, [category, newsTypeList]);

  const isEnd = useMemo(() => {
    return newLists.length >= newsTotal;
  }, [newLists, newsTotal]);

  const { divRef } = useScrollRequest({ scrollTop, isEnd, next: getMoreNews, loading, requestStatus }, [newLists]);

  useEffect(() => {
    setLoading(true);
    if (!params) {
      return;
    }
    clientGetNewsPage(params)
      .then(({ total: allTotal, pageContent }) => {
        setNewsTotal(allTotal);
        // 如果是请求第一页数据，那么就执行覆盖
        if (params.pageNo === 1) {
          setNewLists(pageContent);
        } else {
          setNewLists((res) => {
            return [...res, ...(pageContent || [])];
          });
        }
        setLoading(false);
        requestStatus.current = false;
      })
      .catch(() => {
        setLoading(false);
        requestStatus.current = false;
      });
  }, [params]);

  return (
    <Page title={shareContent.shareTitle}>
      <ScrollPosition onScroll={handleScroll}>
        <div className="bg-white">
          {/* 新闻中心banner */}
          <NewsBanner newsCenterTitle={newsTextData.newsTitleText} newsCenterImg={newsCenterImg} />

          <div className="flex max-w-screen-xl flex-col lg:mx-5 lg:flex-row lg:pt-8 xl:px-0 l:m-auto">
            {/* 新闻类型 */}
            <div
              className={`sticky top-15 z-20 bg-white lg:top-25 lg:h-30r lg:w-[9.25rem] ${isStick ? 'box-s' : false}`}
            >
              <NewsType
                currentTagName={currentTagName}
                onChange={handleNewsTypeChange}
                typeTitle={newsTextData.NewsTypeText}
                typeList={newsTypeList}
              />
            </div>
            {/* 新闻列表 */}
            <div
              ref={divRef}
              className="min-w-0 flex-1 space-y-4 px-5 py-3 pb-8 lg:ml-[80px] lg:space-y-8 lg:px-0 lg:py-0 lg:pb-25"
            >
              {newLists?.map((item, index) => (
                <NewsItemCenter position={index + 1} newsItemData={item} key={index} newsSize="small" />
              ))}
              {loading ? <div className="flex justify-center py-3 text-sm lg:text-[16px]">正在获取更多内容</div> : null}
            </div>
          </div>
        </div>
      </ScrollPosition>
    </Page>
  );
};

export async function getServerSideProps({ locale, query = {} }: any) {
  const { category = '', pageContentNo, preview } = query;
  const params = {
    pageSize: query.pageSize || 10,
    pageNo: 1,
    newsClassifyId: category,
    lanType: locale,
  };
  const newsTypeList = await getNewsCategory({ lanType: locale });
  const pageContentParams = {
    uri: '/news',
    pageContentNo,
    preview,
  };
  const pageContentData = await getPageContent(pageContentParams, locale);
  const { pageContent: newsCenterlist, total } = await getNewsPage(params);

  return {
    props: {
      lxCustom: {
        cid: 'c_donation_tbuxbner',
        locale,
      },
      newsCenterlist,
      total,
      newsTypeList,
      locale,
      pageContentData,
      ...genShareConfig(pageContentData),
      ...(await serverSideTranslation(locale, ['cookie', 'common'])),
    },
  };
}

export default NewsCenter;
